import BacktestingCard from "features/bots/BotForm/Backtesting/BacktestingCard";
import EmptyView from "common/Empty/EmptyView";
import useBacktestingHistory from "@shared/features/bots/BotForm/Backtesting/useBacktestingHistory";
import styled, { useTheme } from "styled-components";
import React, { CSSProperties, useContext } from "react";
import { BacktestingRunType, BotType, LanguageNameSpaces } from "@shared/types";
import ConfirmDeleteModal from "features/bots/BotForm/Backtesting/ConfirmDeleteModal";
import Button from "common/Buttons/Button";
import {
  APPLIED_BACKTESTING_QUERY_PARAM,
  BACKTESTING_BOT_QUERY_PARAM,
} from "features/bots/common/constants";
import useQuery from "utils/hooks/useQuery";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import SeeAllBacktestingsBanner from "features/bots/BotForm/Backtesting/common/SeeAllBacktestingsBanner";
import { EmptyStatesType } from "common/Icons/types.ts";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import FlatList from "common/Lists/FlatList.tsx";
import Flex from "common/Boxes/Flex.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { NEW_LOOK_RADIUS, getPanelShadow } from "common/NewLook/tokens.ts";

interface BacktestingHistoryProps {
  wrapperStyle?: CSSProperties;
}

const BacktestingHistory = ({ wrapperStyle }: BacktestingHistoryProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const query = useQuery();
  const { appendQuery } = useHistoryWithQuery();
  const queryAppliedBacktesting = Number(
    query.get(APPLIED_BACKTESTING_QUERY_PARAM)
  );
  const { isDrawer } = useContext(DrawerContext) || {};
  const isNewLook = theme.isNewLook && !isDrawer;

  const unapplyConfiguration = () => {
    appendQuery(undefined, [
      APPLIED_BACKTESTING_QUERY_PARAM,
      BACKTESTING_BOT_QUERY_PARAM,
    ]);
  };

  const {
    isFetching,
    backtestingRuns,
    backtestingToDelete,
    setBacktestingToDelete,
    appliedBacktesting,
    showDivider,
    botType,
  } = useBacktestingHistory({ queryAppliedBacktesting, unapplyConfiguration });

  const seeAllBanner = (
    <SeeAllBacktestingsBanner
      botType={botType}
      selectedItems={[
        botType === BotType.SIGNAL ? "group" : "symbol",
        "account",
      ]}
      style={{
        padding: isNewLook
          ? "12px 20px 0"
          : isDrawer
          ? "16px 16px 0"
          : "12px 16px 0",
      }}
    />
  );

  const renderTopContainer = () => {
    if (!appliedBacktesting) return null;
    return (
      <div>
        <BacktestingCard
          data={appliedBacktesting}
          setBacktestingToDelete={setBacktestingToDelete}
          isSelected={true}
          botType={botType}
        />
        <Button
          $type="secondary"
          style={{ width: "100%", marginTop: 16 }}
          onClick={unapplyConfiguration}
        >
          {t("backtesting.actions.unapply_configuration")}
        </Button>
        {showDivider && <Divider />}
      </div>
    );
  };

  const renderContent = ({
    item,
    index,
  }: {
    item: BacktestingRunType;
    index: number;
  }) => {
    return (
      <BacktestingCard
        key={index}
        data={item}
        setBacktestingToDelete={setBacktestingToDelete}
        botType={botType}
      />
    );
  };

  const classicEmptyState = !backtestingRuns?.length && !appliedBacktesting && (
    <EmptyView
      iconKey={EmptyStatesType.BacktestingHistory}
      description={t("backtesting.empty_states.no_backtesting_history")}
      iconWidth={110}
    />
  );
  // New look: skeleton cards while loading (no spinner over an empty list),
  // then a short empty state.
  const newLookEmptyState = isFetching ? (
    <SkeletonCards aria-busy={true}>
      {Array.from({ length: 2 }, (_, index) => (
        <SkeletonCard key={index}>
          <Skeleton width="60%" height={12} />
          <Skeleton width={96} height={18} />
          <Skeleton width="100%" height={10} />
        </SkeletonCard>
      ))}
    </SkeletonCards>
  ) : (
    !backtestingRuns?.length &&
    !appliedBacktesting && (
      <PanelEmpty
        title={t("backtesting.empty_states.no_backtesting_history")}
      />
    )
  );
  const emptyState = isNewLook ? newLookEmptyState : classicEmptyState;

  return (
    <Wrapper>
      {seeAllBanner}
      <FlatList
        data={backtestingRuns || []}
        renderContent={renderContent}
        renderTopContainer={renderTopContainer}
        emptyState={emptyState}
        listStyle={{ paddingBottom: 8 }}
        minColumnWidth={isNewLook ? 280 : undefined}
        gap={isNewLook ? 12 : undefined}
        paddingHorizontal={isNewLook ? 20 : undefined}
        loading={isNewLook ? false : isFetching}
        loadingProps={{
          backgroundColor:
            theme.colors[isDrawer ? "layoutBgColor" : "cardBgColor"],
          spinnerMarginTop: 100,
          wrapStyle: { minHeight: 200 },
          spinnerSize: "default",
        }}
        wrapperStyle={wrapperStyle}
      />
      <ConfirmDeleteModal
        closeModal={() => setBacktestingToDelete(undefined)}
        backtestingRun={backtestingToDelete}
        botType={botType}
      />
    </Wrapper>
  );
};

export default BacktestingHistory;

const Wrapper = styled(Flex)`
  flex-direction: column;
  height: 100%;
`;
const Divider = styled.div`
  border-bottom: ${(p) => p.theme.borders.light};
  margin-bottom: 12px;
  padding-top: 16px;
`;
const SkeletonCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: ${(p) => getPanelShadow(p.theme)};
`;
